import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { isValidEmail } from "utils/Validation/isValidEmail";
import { getErrorMessage } from "utils/errorHelpers";
import { ContactRole } from "client/api-v2";
import type { ContactDto } from "client/api-v2";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { addBillingAccountContact, updateBillingAccountContact } from "../services/billingAccountService";

interface ContactDialogProps {
  open: boolean;
  billingAccountCode: string;
  /** The contact to edit. Omitted, the dialog adds a new one. */
  contact?: ContactDto;
  onCancel: () => void;
  onSaved: (contact: ContactDto) => void;
}

interface ContactFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roles: ContactRole[];
}

const formId = "contact-form";

const roleDescriptions: Record<ContactRole, string> = {
  [ContactRole.Billing]: "receives invoices",
  [ContactRole.Technical]: "practitioners who run Octopus",
  [ContactRole.Security]: "receives security / vulnerability notifications",
};

const emptyValues: ContactFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  roles: [],
};

function formValuesFor(contact: ContactDto | undefined): ContactFormValues {
  if (!contact) {
    return emptyValues;
  }

  return {
    firstName: contact.firstName,
    lastName: contact.lastName ?? "",
    email: contact.email,
    phone: contact.phone ?? "",
    roles: [...contact.roles],
  };
}

export function ContactDialog({ open, billingAccountCode, contact, onCancel, onSaved }: ContactDialogProps) {
  const [serverError, setServerError] = useState<string | undefined>();
  const { isAuthorized } = useRbacContext();
  const canSave = isAuthorized(RbacPolicies.BillingAccounts.ReadWrite);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ContactFormValues>({ defaultValues: emptyValues });

  useEffect(() => {
    if (open) {
      reset(formValuesFor(contact));
      setServerError(undefined);
    }
  }, [open, contact, reset]);

  const onSubmit = async (values: ContactFormValues) => {
    setServerError(undefined);
    const saveContact = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim() || null,
      email: values.email.trim(),
      phone: values.phone.trim() || null,
      roles: values.roles,
    };

    try {
      onSaved(
        contact
          ? await updateBillingAccountContact(billingAccountCode, contact.id, saveContact)
          : await addBillingAccountContact(billingAccountCode, saveContact)
      );
    } catch (e) {
      setServerError(getErrorMessage(e));
    }
  };

  const cancel = () => !isSubmitting && onCancel();

  return (
    <Dialog open={open} onClose={cancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {contact ? "Edit contact" : "Add contact"}
        <IconButton
          aria-label="close"
          onClick={cancel}
          disabled={isSubmitting}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            {/* Save failures — a duplicate email being the common one — are reported by the API
                against the whole request rather than a field, so they surface here rather than
                under any one input. */}
            {serverError && (
              <Grid size={12}>
                <Alert severity="error">{serverError}</Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="firstName"
                control={control}
                rules={{ validate: (value) => value.trim() !== "" || "First name is required" }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    required
                    fullWidth
                    label="First name"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="lastName"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField {...rest} inputRef={ref} fullWidth label="Last name" />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="email"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value.trim() === "") {
                      return "Email is required";
                    }
                    return isValidEmail(value) || "Email must be a valid email address";
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    required
                    fullWidth
                    label="Email"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="phone"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField {...rest} inputRef={ref} fullWidth label="Phone" placeholder="+1 732 555 0114" />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="roles"
                control={control}
                rules={{ validate: (value) => value.length > 0 || "At least one role is required" }}
                render={({ field: { value, onChange }, fieldState }) => (
                  <FormControl component="fieldset" error={fieldState.invalid}>
                    <FormLabel component="legend" sx={{ typography: "body1" }}>
                      Roles*{" "}
                      <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                        - at least 1 required
                      </Typography>
                    </FormLabel>
                    <FormGroup>
                      {Object.values(ContactRole).map((role) => (
                        <FormControlLabel
                          key={role}
                          control={
                            <Checkbox
                              checked={value.includes(role)}
                              onChange={(event) =>
                                onChange(
                                  event.target.checked ? [...value, role] : value.filter((chosen) => chosen !== role)
                                )
                              }
                            />
                          }
                          label={
                            <>
                              {role}{" "}
                              <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                                — {roleDescriptions[role]}
                              </Typography>
                            </>
                          }
                        />
                      ))}
                    </FormGroup>
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </form>
        <Grid size={12}>
          <Divider />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" form={formId} variant="contained" color="primary" disabled={isSubmitting || !canSave}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
